/**
 * Rollen an einem Asset — viele auf einmal, und „Ersetzen durch …".
 *
 * Gemeldet im Termin mit der HOWOGE am 07.10.2026 (Punkt 16): wer eine neue
 * Applikation einführt, ordnet sie zwanzig oder dreißig Rollen zu. Bisher
 * ging das nur von der Rolle aus, also zwanzig- oder dreißigmal der
 * Rolleneditor. Und wer eine Anwendung durch eine andere ablöst, trug jede
 * Zuordnung von Hand um.
 *
 * Zwei Wege der Schnittstelle, beide **eine** Transaktion — alles oder
 * nichts:
 *
 * - `POST /api/zuordnungen/_sammeln` legt Zuordnungen an und archiviert
 *   andere, für genau ein Asset.
 * - `POST /api/zuordnungen/_ersetzen` überträgt alle Zuordnungen von einem
 *   Asset auf ein anderes gleicher Objektart und archiviert auf Wunsch das
 *   alte.
 *
 * Beide kennen einen Trockenlauf, der nichts schreibt und nur zählt.
 */

import { senden } from './klient.js';
import { tokenHolen } from '../anmeldung/msal.js';

/** Höchstzahl je Sammelvorgang — darüber weist die Schnittstelle mit 400 ab. */
export const SAMMEL_HOECHSTENS = 500;

/* ------------------------------------------------------------ Sammeln */

export interface Sammelanfrage {
  asset: string;
  assetTyp: string;
  kategorie: string;
  hinzufuegen: { rolleId: string; berechtigungsart: string }[];
  /** Je Zeile die Version — eine veraltete ergibt 409, und nichts ist geschrieben. */
  entfernen: { berechtigungId: string; version: string }[];
  /** Pflicht (mindestens fünf Zeichen), sobald etwas entfernt wird. */
  begruendung: string;
  vorgangId?: string;
  trockenlauf?: boolean;
}

/** Eine neue Zuordnung — angelegt oder zur Freigabe eingereicht (dann ohne Kennung). */
export interface Sammelneu { rolleId: string; berechtigungsart: string; berechtigungId: string | null }
/** Eine entfernte Zuordnung — archiviert oder zur Freigabe eingereicht. */
export interface Sammelweg { berechtigungId: string; rolleId: string }

export interface Sammelergebnis {
  vorgangId: string;
  /** Mindestens ein Teil liegt zur Freigabe (Antwort 202). */
  freigabe: boolean;
  angelegt: Sammelneu[];
  eingereicht: Sammelneu[];
  /** Schon vorhanden oder schon beantragt — mit Grund. */
  uebersprungen: { rolleId: string; grund: string }[];
  archiviert: Sammelweg[];
  entfernungEingereicht: Sammelweg[];
}

/** Der Rumpf einer 409: welche Zeilen veraltet sind, beim Ersetzen der neue Stand. */
export interface Sammelkonflikt { fehler: string; konflikte?: string[]; stand?: string }

export async function zuordnungenSammeln(
  mandant: string,
  anfrage: Sammelanfrage,
): Promise<Sammelergebnis> {
  return senden<Sammelergebnis>('/api/zuordnungen/_sammeln', 'POST', {
    mandant,
    token: await tokenHolen(),
    rumpf: { trockenlauf: false, ...anfrage },
  });
}

/** Die Rückmeldung zu einem Sammelvorgang — ein Satz, nach Wirkung getrennt. */
export function sammelText(e: Sammelergebnis): string {
  const teile: string[] = [];
  const n = (zahl: number, eins: string, viele: string) => `${zahl} ${zahl === 1 ? eins : viele}`;
  if (e.angelegt.length) teile.push(n(e.angelegt.length, 'Rolle zugeordnet', 'Rollen zugeordnet'));
  if (e.eingereicht.length) {
    teile.push(n(e.eingereicht.length, 'Zuordnung liegt', 'Zuordnungen liegen') + ' zur Freigabe');
  }
  if (e.archiviert.length) {
    teile.push(n(e.archiviert.length, 'Zuordnung archiviert', 'Zuordnungen archiviert'));
  }
  if (e.entfernungEingereicht.length) {
    teile.push(n(e.entfernungEingereicht.length, 'Entfernung liegt', 'Entfernungen liegen')
      + ' zur Freigabe');
  }
  if (e.uebersprungen.length) {
    teile.push(n(e.uebersprungen.length, 'Rolle übersprungen', 'Rollen übersprungen')
      + ' (schon vorhanden oder beantragt)');
  }
  return teile.length ? teile.join(' · ') + '.' : 'Es gab nichts zu ändern.';
}

/* ------------------------------------------------------------ Ersetzen */

/**
 * Ein Asset in den Zuordnungen. Die Kategorie gehört dazu: AVD- und
 * Surface-Fassung einer Anwendung sind zwei Applikationen gleichen Namens
 * (HOWOGE, Punkt 6) und unterscheiden sich nur in ihr.
 */
export interface Assetbezug { asset: string; assetTyp: string; kategorie?: string }

export interface Ersetzanfrage {
  von: Assetbezug;
  nach: Assetbezug;
  /** Das alte Asset archivieren — `null` lässt es stehen. */
  altesAsset: { assetId: string; version: string } | null;
  begruendung: string;
  vorgangId?: string;
  trockenlauf: boolean;
  /** Aus dem Trockenlauf; weicht der Bestand inzwischen ab, kommt 409. */
  stand?: string;
}

export interface Ersetzergebnis {
  vorgangId: string;
  freigabe: boolean;
  uebernommen: number;
  uebersprungen: number;
  archiviert: number;
  assetArchiviert: boolean;
  stand: string;
}

export async function assetErsetzen(
  mandant: string,
  anfrage: Ersetzanfrage,
): Promise<Ersetzergebnis> {
  return senden<Ersetzergebnis>('/api/zuordnungen/_ersetzen', 'POST', {
    mandant,
    token: await tokenHolen(),
    rumpf: anfrage,
  });
}
